import React, { useState, useRef, useEffect } from 'react';
import { User, LogOut, Trash2, Trophy, ChevronDown, LogIn, Database, Map, MessageSquare, BookOpen, Flame, Droplets, Mountain, Wind, Users, ShieldAlert } from 'lucide-react';

export const NavUserMenu = ({ user, data, onLogout, onLogin, onReset, onShowBadges, onShowHelp, onShowEducation, onShowProfile, onShowTerms, isSaving }) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Helper to get avatar icon
    const getAvatarIcon = (element) => {
        switch(element) {
            case 'fire': return <Flame className="w-4 h-4 text-orange-400" />;
            case 'water': return <Droplets className="w-4 h-4 text-blue-400" />;
            case 'earth': return <Mountain className="w-4 h-4 text-emerald-400" />;
            default: return <Wind className="w-4 h-4 text-cyan-400" />; // Air default
        }
    };

    // --- EMAIL ACTION ---
    const handleSendFeedback = () => {
        const email = "fourelementsfinancellc@gmail.com";
        const subject = encodeURIComponent("Four Elements Finance - User Feedback");
        const body = encodeURIComponent(
`Hi Four Elements Team,

[ ] Bug Report
[ ] Feature Request
[ ] General Question

What were you trying to do?
- 

Which Tab were you on?
- 

Device / Browser (e.g. iPhone Safari, Laptop Chrome):
- 

Additional Notes:
- 
`
        );
        
        setIsOpen(false);
        window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    };

    return (
        <div className="relative" ref={menuRef}>
            <button 
                onClick={() => setIsOpen(!isOpen)} 
                className="flex items-center gap-2 hover:bg-slate-800 rounded-full p-1 pl-3 pr-2 transition-colors border border-transparent hover:border-slate-700"
            >
                {/* --- UPDATED AVATAR LOGIC --- */}
                {user ? (
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border border-slate-700 bg-slate-900`}>
                        {getAvatarIcon(data?.elementalAvatar || 'air')}
                    </div>
                ) : (
                    <div className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center text-slate-400">
                        <User className="w-4 h-4" />
                    </div>
                )}
                <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute right-0 top-12 w-56 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden animate-fade-in">
                    
                    {/* User Info Header */}
                    <div className="p-4 border-b border-slate-800 bg-slate-950/50">
                        {user ? (
                            <div>
                                <p className="text-white text-sm font-bold truncate">{data?.username || user.email}</p>
                                <p className="text-xs flex items-center gap-1 mt-1">
                                    {isSaving ? (
                                        <>
                                            <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span> 
                                            <span className="text-yellow-500">Syncing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="w-2 h-2 bg-emerald-500 rounded-full"></span> 
                                            <span className="text-emerald-400">Cloud Synced</span>
                                        </>
                                    )}
                                </p>
                            </div>
                        ) : (
                            <div>
                                <p className="text-white text-sm font-bold">Guest Mode</p>
                                <p className="text-xs text-orange-400 flex items-center gap-1 mt-1">
                                    <Database className="w-3 h-3" /> Local Save Only
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="p-2">
                        {/* --- NEW PROFILE BUTTON --- */}
                        <button onClick={() => { onShowProfile(); setIsOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white rounded-lg transition-colors">
                            <Users className="w-4 h-4 text-indigo-400" /> Social & Profile
                        </button>

                        <div className="h-px bg-slate-800 mx-2 my-1"></div>

                        <button onClick={() => { onShowBadges(); setIsOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white rounded-lg transition-colors">
                            <Trophy className="w-4 h-4 text-yellow-500" /> Mastery Badges
                        </button>
                        
                        <button onClick={() => { onShowEducation(); setIsOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white rounded-lg transition-colors">
                            <BookOpen className="w-4 h-4 text-indigo-400" /> Knowledge Base
                        </button>

                        <button onClick={() => { onShowHelp(); setIsOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white rounded-lg transition-colors">
                            <Map className="w-4 h-4 text-blue-400" /> Replay App Tour
                        </button>
                        
                        <button onClick={handleSendFeedback} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white rounded-lg transition-colors">
                            <MessageSquare className="w-4 h-4 text-emerald-400" /> Send Feedback
                        </button>

                        <button onClick={() => { onShowTerms(); setIsOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white rounded-lg transition-colors">
                            <ShieldAlert className="w-4 h-4 text-slate-400" /> Terms & Privacy
                        </button>
                    </div>

                    <div className="h-px bg-slate-800 mx-2 my-1"></div>

                    {/* Danger Zone */}
                    <div className="p-2">
                        <button onClick={() => { onReset(); setIsOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-400 hover:bg-red-900/20 hover:text-red-400 rounded-lg transition-colors">
                            <Trash2 className="w-4 h-4" /> Reset All Data
                        </button>
                        
                        {user ? (
                            <button onClick={() => { onLogout(); setIsOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-400 hover:bg-slate-800 hover:text-white rounded-lg transition-colors">
                                <LogOut className="w-4 h-4" /> Log Out
                            </button>
                        ) : (
                            <button onClick={() => { onLogin(); setIsOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors">
                                <LogIn className="w-4 h-4" /> Login / Save
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};